import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IconButton, CardMedia, Grid, TextField, MenuItem } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShareIcon from '@mui/icons-material/Share';
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import './productStyle.css';
import './shareMenu.css';
import { addItem, updateQuantity, removeItem, calculateTotalAmount } from '../../store/cartSlice';
import { processUrl } from 'common/Service';
import { useHistory } from 'react-router-dom';




const Product = (props: any) => {
    const history = useHistory();
    const dispatch = useDispatch();

    // State for lazy loading
    const [displayedProducts, setDisplayedProducts] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const productsPerPage = 12; // Number of products to load each time

    const [filterCriteria, setFilterCriteria] = React.useState({ brandName: '', manufacturer: '', mrp: '' });
    const [sortOrder, setSortOrder] = React.useState(''); // Ascending or Descending
    const { products } = props;

    // Load initial products
    useEffect(() => {
        if (products && products.length > 0 && initialLoad) {
            const initialProducts = products.slice(0, productsPerPage);
            setDisplayedProducts(initialProducts);
            setInitialLoad(false);
        }
    }, [products, initialLoad]);

    const redirectToProductDetails = (product: any) => {
        // Save the selected product to local storage
        localStorage.setItem('selectedProduct', JSON.stringify(product));
        // Force a navigation to the product details page
        window.location.href = `/product-details/${product._id}`;
    };

    const handleShare = async (e: React.MouseEvent, product: any) => {
        e.stopPropagation();
        const shareUrl = `${window.location.origin}/product-details/${product._id}`;
        const shareText = `Check out ${product.title} on our store!`;
        const encodedText = encodeURIComponent(`${shareText}\n\n${shareUrl}`);

        // Remove any existing share menu
        const existingMenu = document.querySelector('.share-menu');
        if (existingMenu) {
            document.body.removeChild(existingMenu);
        }

        // Create sharing menu
        const shareMenu = document.createElement('div');
        shareMenu.className = 'share-menu';

        // Get product image URL if available
        let imageUrl = '';
        if (product.imageUrl) {
            imageUrl = processUrl(product.imageUrl);
        }

        // Share options
        const createShareButton = (name: string, className: string, onClick: () => void) => {
            const button = document.createElement('button');
            button.textContent = name;
            button.className = `share-button ${className}`;
            button.addEventListener('click', () => {
                onClick();
                document.body.removeChild(shareMenu);
            });
            return button;
        };

        // WhatsApp sharing
        const whatsappButton = createShareButton('Share on WhatsApp', 'whatsapp', () => {
            const whatsappUrl = imageUrl
                ? `https://wa.me/?text=${encodedText}%0A${encodeURIComponent(imageUrl)}`
                : `https://wa.me/?text=${encodedText}`;
            window.open(whatsappUrl, '_blank');
        });
        shareMenu.appendChild(whatsappButton);

        // Facebook sharing
        const facebookButton = createShareButton('Share on Facebook', 'facebook', () => {
            window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
        });
        shareMenu.appendChild(facebookButton);

        // Twitter sharing
        const twitterButton = createShareButton('Share on Twitter', 'twitter', () => {
            window.open(`https://twitter.com/intent/tweet?text=${encodedText}`, '_blank');
        });
        shareMenu.appendChild(twitterButton);

        // Copy link button
        const copyButton = createShareButton('Copy Link', 'copy', async () => {
            try {
                await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
                toast.success('Link copied to clipboard!');
            } catch (error) {
                toast.error('Failed to copy link');
            }
        });
        shareMenu.appendChild(copyButton);

        // Position menu near the click
        const rect = (e.target as HTMLElement).getBoundingClientRect();
        const menuTop = rect.bottom + window.scrollY;
        const menuLeft = rect.left;

        // Ensure menu stays within viewport
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        shareMenu.style.top = `${Math.min(menuTop, viewportHeight - 300)}px`;
        shareMenu.style.left = `${Math.min(menuLeft, viewportWidth - 250)}px`;

        // Add the menu to the page
        document.body.appendChild(shareMenu);

        // Close menu when clicking outside
        const closeMenu = (event: MouseEvent) => {
            if (!shareMenu.contains(event.target as Node)) {
                document.body.removeChild(shareMenu);
                document.removeEventListener('click', closeMenu);
            }
        };

        // Add a small delay before adding the click listener to prevent immediate closure
        setTimeout(() => {
            document.addEventListener('click', closeMenu);
        }, 100);

        // Native sharing as fallback for mobile devices
        if (navigator.share) {
            try {
                if (product.imageUrl) {
                    try {
                        const response = await fetch(imageUrl);
                        if (response.ok) {
                            const blob = await response.blob();
                            const file = new File([blob], 'product-image.jpg', { type: blob.type || 'image/jpeg' });
                            await navigator.share({
                                title: product.title,
                                text: shareText,
                                url: shareUrl,
                                files: [file]
                            });
                            return;
                        }
                    } catch (error) {
                        console.error('Image sharing failed:', error);
                    }
                }

                await navigator.share({
                    title: product.title,
                    text: shareText,
                    url: shareUrl
                });
            } catch (error) {
                console.error('Native sharing failed:', error);
            }
        }
    };

    // Handle infinite scroll
    const handleScroll = useCallback(() => {
        if (loading || initialLoad) return;

        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = document.documentElement.clientHeight;

        // Start loading when user is 200px from bottom
        if (scrollHeight - scrollTop - clientHeight < 200) {
            setCurrentPage(prev => prev + 1);
        }
    }, [loading, initialLoad]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Load more products when currentPage changes
    useEffect(() => {
        if (initialLoad) return; // Skip if it's initial load

        const loadMoreProducts = async () => {
            setLoading(true);
            const start = (currentPage - 1) * productsPerPage;
            const end = start + productsPerPage;

            // Simulate network delay for smooth loading animation
            await new Promise(resolve => setTimeout(resolve, 800));

            const newProducts = sortedProducts.slice(start, end);
            if (newProducts.length > 0) {
                setDisplayedProducts(prev => [...prev, ...newProducts]);
            }
            setLoading(false);
        };

        loadMoreProducts();
    }, [currentPage, sortOrder, filterCriteria, initialLoad]);

    const cartItems = useSelector((state: any) => state.cart.items);
    const handleAddToCart = (product: any) => {
        const existingItem = cartItems.find((item: any) => item.product._id === product._id);
        if (existingItem) {
            dispatch(updateQuantity({
                _id: product._id,
                quantity: existingItem.quantity + 1
            }));
        } else {
            dispatch(addItem({
                product: product
            }));
        }
        dispatch(calculateTotalAmount());
        toast.success(`${product.title} added to cart`);
    };

    const handleRemoveFromCart = (product: any) => {
        const existingItem = cartItems.find((item: any) => item.product._id === product._id);
        if (existingItem && existingItem.quantity > 1) {
            dispatch(updateQuantity({
                _id: product._id,
                quantity: existingItem.quantity - 1
            }));
        } else {
            dispatch(removeItem({ _id: product._id }));
        }
        dispatch(calculateTotalAmount());
        toast.warn(`${product.title} removed from cart`);
    };

    const uniqueBrandNames = Array.from(new Set(products.map((product: any) => product.brand)));
    const uniqueManufacturers = Array.from(new Set(products.map((product: any) => product.manufacturer)));
    const filteredProducts = products.filter((product: any) => {
        const { brandName, manufacturer } = filterCriteria;
        return (
            (!brandName || product.brand.toLowerCase() === brandName.toLowerCase()) &&
            (!manufacturer || product.manufacturer.toLowerCase() === manufacturer.toLowerCase())
        );
    });

    const sortedProducts = filteredProducts.slice().sort((a: any, b: any) => {
        if (sortOrder === 'highToLow') {
            return b.mrp - a.mrp;
        } else if (sortOrder === 'lowToHigh') {
            return a.mrp - b.mrp;
        }
        return 0;
    });


    function calculatePriceDifferencePercentage(mrp: number, sellingPrice: number) {
        const priceDifference = mrp - sellingPrice;
        // Calculate the percentage difference
        const percentageDifference = (priceDifference / mrp) * 100;
        return percentageDifference.toFixed(0); // Return with two decimal places
    }


    return (
        <React.Fragment >
            <div style={{ margin: "5px", padding: "5px" }}>

                <Grid container spacing={2} >
                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            select
                            label="Filter By Brand"
                            variant="outlined"
                            value={filterCriteria.brandName}
                            onChange={(e) => setFilterCriteria({ ...filterCriteria, brandName: e.target.value })}
                        >
                            <MenuItem value="">All</MenuItem>
                            {uniqueBrandNames.map((brandName: any, index: any) => (
                                <MenuItem key={index} value={brandName}>{brandName}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            select
                            label="Filter by Manufacturer"
                            variant="outlined"
                            value={filterCriteria.manufacturer}
                            onChange={(e) => setFilterCriteria({ ...filterCriteria, manufacturer: e.target.value })}
                        >
                            <MenuItem value="">All</MenuItem>
                            {uniqueManufacturers.map((manufacturer: any, index: any) => (
                                <MenuItem key={index} value={manufacturer}>{manufacturer}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={5}>

                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            select
                            label="Sort by MRP"
                            variant="outlined"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                        >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="highToLow">High to Low</MenuItem>
                            <MenuItem value="lowToHigh">Low to High</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
                <style>
                    {`
                        @keyframes fadeInUp {
                            from {
                                opacity: 0;
                                transform: translateY(20px);
                            }
                            to {
                                opacity: 1;
                                transform: translateY(0);
                            }
                        }
                        .product-item {
                            animation: fadeInUp 0.5s ease-out;
                        }
                        @keyframes shimmer {
                            0% {
                                background-position: -468px 0;
                            }
                            100% {
                                background-position: 468px 0;
                            }
                        }
                        .loading-placeholder {
                            animation: shimmer 1s linear infinite forwards;
                            background: linear-gradient(to right, #f6f7f8 8%, #edeef1 18%, #f6f7f8 33%);
                            background-size: 800px 104px;
                            height: 200px;
                            border-radius: 4px;
                            margin-bottom: 20px;
                        }
                    `}
                </style>
                <Grid container >
                    {displayedProducts.map((product: any) => (
                        <Grid item xs={12} sm={6} md={3} key={product.id} spacing={2} className="product-item">
                            <div className="pcards-container" key={product._id}
                                onClick={() => redirectToProductDetails(product)}
                            >
                                <div className="pcard" style={{ position: 'relative' }}>
                                    <IconButton
                                        size='small'
                                        onClick={(e) => handleShare(e, product)}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '10px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            zIndex: 10,
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        <ShareIcon fontSize='small' style={{ color: '#0ea04e' }} />
                                    </IconButton>
                                    <div className="ribbon-wrapper">
                                        <div className="ribbon-pop">{calculatePriceDifferencePercentage(product.mrp, product.sellingPrice)}% OFF</div>
                                    </div>
                                    <div className="pcard-media">
                                        <div >
                                            <CardMedia
                                                sx={{ height: 210 }}
                                                image={processUrl(product.imageUrl)}
                                                title={product.title}
                                            />
                                        </div>

                                    </div>
                                    <div className="pcard-description">
                                        <div className="about-place">
                                            <div className="place">
                                                <div>
                                                    <h4 className="place-name">
                                                        {product.title}
                                                    </h4>
                                                </div>
                                                <p className="place-speciality" style={{ paddingBottom: "8px" }}>{product.title}&nbsp;:{product.brand}</p>
                                            </div>
                                            <div className="place-review">
                                                <div>
                                                    <span style={{ fontWeight: "bold" }}>
                                                        <del style={{ fontWeight: "lighter" }}>
                                                            ₹{product.mrp}
                                                        </del>
                                                        ₹{product.sellingPrice}
                                                    </span>
                                                </div>
                                                <div className="quantity-control" onClick={(e) => e.stopPropagation()}>
                                                    {cartItems.find((item: any) => item.product._id === product._id)?.quantity > 0 && (
                                                        <IconButton
                                                            size='small'
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveFromCart(product);
                                                            }}
                                                            style={{ borderRadius: "5px", color: "white", backgroundColor: "#0ea04e", }}
                                                        >
                                                            <RemoveIcon fontSize='small' />
                                                        </IconButton>
                                                    )}
                                                    {cartItems.find((item: any) => item.product._id === product._id)?.quantity > 0 && (
                                                        <div className="quantity">
                                                            {cartItems.find((item: any) => item.product._id === product._id)?.quantity || 0}
                                                        </div>
                                                    )}
                                                    <IconButton
                                                        size='small'
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleAddToCart(product);
                                                        }}
                                                        style={{ borderRadius: "5px", color: "white", backgroundColor: "#0ea04e" }}
                                                    >
                                                        <AddIcon fontSize='small' />
                                                    </IconButton>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Grid>
                    ))}
                </Grid>
                {loading && (
                    <div style={{
                        width: '100%',
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '15px'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                            gap: '20px',
                            width: '100%',
                            maxWidth: '1200px'
                        }}>
                            {[1, 2, 3, 4].map((placeholder) => (
                                <div key={placeholder} className="loading-placeholder"></div>
                            ))}
                        </div>
                        <div className="loading-spinner">
                            <style>
                                {`
                                    @keyframes spin {
                                        0% { transform: rotate(0deg); }
                                        100% { transform: rotate(360deg); }
                                    }
                                    .loading-spinner {
                                        width: 40px;
                                        height: 40px;
                                        border: 3px solid rgba(14, 160, 78, 0.1);
                                        border-radius: 50%;
                                        border-top-color: #0ea04e;
                                        animation: spin 1s linear infinite;
                                    }
                                `}
                            </style>
                        </div>
                    </div>
                )}
            </div>
        </React.Fragment >
    );
};

export default Product;
